import { CircleAlertIcon, TriangleAlertIcon } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Frame,
  FrameDescription,
  FrameHeader,
  FramePanel,
  FrameTitle,
  FrameAction,
} from "@/components/ui/frame";
import { Skeleton } from "@/components/ui/skeleton";
import { RelativeTime } from "./RelativeTime";
import { fmtClock, fmtDuration, fmtNum } from "./format";
import type { FleetStatus } from "./types";

// The autoscaler runs every 5 min and a scale-up is a fresh deployment, so a
// backlog sitting on the floor fleet for a few minutes is normal.
const STUCK_WARN_MS = 15 * 60_000;

function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5 p-3">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="font-mono text-lg font-medium tabular-nums">{value}</span>
      {hint && <span className="truncate text-xs text-muted-foreground">{hint}</span>}
    </div>
  );
}

/**
 * The analysis backlog exactly as the autoscaler cron sees it (queued +
 * in flight on the `analysis` queue), plus the source queue and the contact
 * backfill. Replica counts are not here: they live in Railway, not Redis —
 * with one task per worker, "in flight" is the number of busy workers.
 */
export function FleetPanel({
  fleet,
  stuckSince,
}: {
  fleet: FleetStatus | null | undefined;
  stuckSince: number | null;
}) {
  const stuckFor = stuckSince ? Date.now() - stuckSince : 0;

  return (
    <Frame className="@container">
      <FrameHeader>
        <FrameTitle>Analysis fleet</FrameTitle>
        <FrameDescription>
          Read from this deployment's Redis the way the autoscaler reads it.
        </FrameDescription>
        <FrameAction>
          {fleet && (
            <Badge variant="outline" className="font-mono">
              {fleet.environment}
            </Badge>
          )}
        </FrameAction>
      </FrameHeader>

      {!fleet ? (
        <FramePanel className="p-3">
          <Skeleton className="h-16 w-full rounded-lg" />
        </FramePanel>
      ) : !fleet.available ? (
        <FramePanel className="flex items-center gap-2 p-4 text-sm text-destructive">
          <CircleAlertIcon className="size-4 shrink-0" />
          {fleet.error}
        </FramePanel>
      ) : (
        <>
          <FramePanel className="grid grid-cols-2 divide-x divide-y p-0 @xl:grid-cols-3 @4xl:grid-cols-5 @4xl:divide-y-0">
            <Stat
              label="Analysis queued"
              value={fmtNum(fleet.analysis.queued)}
              hint={`backlog ${fmtNum(fleet.analysis.backlog)}`}
            />
            <Stat
              label="Analysis in flight"
              value={fmtNum(fleet.analysis.in_flight)}
              hint="≈ busy workers"
            />
            <Stat
              label="Floor absorbs"
              value={fmtNum(fleet.autoscaler.floor_capacity)}
              hint={`${fleet.autoscaler.floor_replicas} workers × ${fleet.autoscaler.tasks_per_replica} tasks`}
            />
            <Stat
              label="Source queue"
              value={`${fmtNum(fleet.source.queued)} / ${fmtNum(fleet.source.in_flight)}`}
              hint="queued / running"
            />
            <Stat
              label="Contact backfill"
              value={`${fmtNum(fleet.analysis_contacts.queued)} / ${fmtNum(fleet.analysis_contacts.in_flight)}`}
              hint="queued / running"
            />
          </FramePanel>
          <FramePanel className="px-3 py-2 text-xs text-muted-foreground">
            Above {fmtNum(fleet.autoscaler.floor_capacity)} queued + in flight
            the autoscaler (every 5 min) switches whole services on, up to{" "}
            {fmtNum(fleet.autoscaler.max_replicas)} workers.
            {fleet.autoscaler.idle_since && (
              <>
                {" "}Queue empty since{" "}
                <RelativeTime at={fleet.autoscaler.idle_since} />.
              </>
            )}
            {fleet.autoscaler.tail_since && (
              <>
                {" "}Only a tail of work left since{" "}
                <RelativeTime at={fleet.autoscaler.tail_since} />.
              </>
            )}
          </FramePanel>
        </>
      )}

      {fleet?.available && stuckSince && (
        <Alert variant={stuckFor >= STUCK_WARN_MS ? "warning" : "info"} className="mt-1">
          <AlertTitle>
            <TriangleAlertIcon />
            Backlog above the floor, but the fleet is not scaling
          </AlertTitle>
          <AlertDescription>
            {fmtNum(fleet.analysis.backlog)} tasks wait while only{" "}
            {fmtNum(fleet.analysis.in_flight)} run (seen since{" "}
            {fmtClock(stuckSince)}, {fmtDuration(stuckFor)}). A scale-up takes
            one autoscaler run plus a deploy; if this lasts past ~15 min, check
            the analysis-autoscaler cron's logs on staging.
          </AlertDescription>
        </Alert>
      )}
    </Frame>
  );
}
